/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect } from "react";

// react-router components
import { useLocation, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Breadcrumbs from "components/Breadcrumbs";
import LogoutIcon from '@mui/icons-material/Logout';
// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
} from "components/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";
import { Tooltip } from "@mui/material";
import { GeneralFetch } from "Api/generalFetch/generalFetch";
import { getLayoutByDevice } from "hooks/mobileCheck/useCheckMobile";

function DashboardNavbar({ absolute, light, isMini }) {
  const [controller, dispatch] = useMaterialUIController();
  const { transparentNavbar, fixedNavbar, darkMode, miniSidenav } = controller;
  const route = useLocation().pathname?.split("/").slice(1);
  const navigate = useNavigate()
  const { fetchData } = GeneralFetch()

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  async function logout() {
    navigate('/')
    await fetchData({}, 'logout', 'post', 'logout')
    sessionStorage.clear();
  }

  useEffect(() => {
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const verticalLayout = getLayoutByDevice();

  return (
    <AppBar
      position={'sticky'}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }} >
          <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })} display="flex" alignItems="center">
            <IconButton
              size="small"
              disableRipple
              color="inherit"
              sx={(theme) => ({
                ...navbarIconButton(theme),
                display: { xs: "inline-block", xl: "none" },
                mr: 1
              })}
              onClick={handleMiniSidenav}
            >
              {miniSidenav ? <MenuIcon /> : <MenuOpenIcon />}
            </IconButton>
            <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
          </MDBox>
          {isMini ? null : (
            <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
              <MDBox color={light ? "white" : "inherit"}>
                {(sessionStorage.getItem('acesso') == '3' && !verticalLayout) && < IconButton
                  size="large"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  aria-controls="notification-menu"
                  aria-haspopup="true"
                  variant="contained"
                  onClick={() => { navigate('/settings') }}
                >
                  <SettingsIcon />
                </IconButton>}

                <Tooltip title="Sair" >
                  <IconButton onClick={async () => await logout()} sx={navbarIconButton} size="medium" disableRipple>
                    <LogoutIcon style={{ color: "#F03A47" }} />
                  </IconButton>
                </Tooltip>
              </MDBox>
            </MDBox>
          )}
        </div>
      </Toolbar>
    </AppBar >
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
